/**
 * JobsByOrchestrationIdContext
 *
 * Historically every `OrchestrationRow` issued its own `/jobs` request filtered to a single
 * orchestration ID, so a section with N orchestrations fired N sequential/parallel round trips.
 * This context batches all orchestration IDs for the whole inference-models tree into a small,
 * fixed number of `/jobs` requests (see `useJobsByOrchestrationIds`) and exposes the results as a
 * single `orchestrationId -> Job` lookup map so descendants can read jobs synchronously instead of
 * each mounting its own query.
 */
import { flatMap, map, values } from 'es-toolkit/compat';
import React, { createContext, useContext, useMemo } from 'react';

import { ModelInference, OrchestrationInference } from 'interfaces/calculateFeatures';
import { Job } from 'interfaces/job';
import { ModelsByStainByType } from 'utils/models';
import { OrchestrationByStainByResultName } from 'utils/orchestrationSelections';
import { useJobsByOrchestrationIds } from 'utils/queryHooks/jobs/useJobs';

export interface JobsByOrchestrationIdContextValue {
  jobsByOrchestrationId: Record<string, Job>;
  isLoadingJobs: boolean;
  isFetchingJobs: boolean;
}

const defaultContextValue: JobsByOrchestrationIdContextValue = {
  jobsByOrchestrationId: {},
  isLoadingJobs: false,
  isFetchingJobs: false,
};

const JobsByOrchestrationIdContext = createContext<JobsByOrchestrationIdContextValue>(defaultContextValue);

export const useJobsByOrchestrationIdContext = () => useContext(JobsByOrchestrationIdContext);

export const collectOrchestrationIdsFromModelsByStain = (modelsByStain: ModelsByStainByType = {}): string[] =>
  flatMap(values(modelsByStain), (modelsByType) =>
    flatMap(values(modelsByType), (modelsByUrl) =>
      flatMap(values(modelsByUrl), (modelInference: ModelInference) =>
        map(modelInference?.orchestrations, 'orchestrationId')
      )
    )
  );

export const collectOrchestrationIdsFromOrchestrationsByStain = (
  orchestrationsByStain: OrchestrationByStainByResultName = {}
): string[] =>
  flatMap(values(orchestrationsByStain), (orchestrationsByResultName) =>
    flatMap(values(orchestrationsByResultName), (orchestrations: OrchestrationInference[]) =>
      map(orchestrations, 'orchestrationId')
    )
  );

export interface JobsByOrchestrationIdProviderProps {
  studyId: string;
  orchestrationIds: string[];
}

export const JobsByOrchestrationIdProvider: React.FC<React.PropsWithChildren<JobsByOrchestrationIdProviderProps>> = ({
  studyId,
  orchestrationIds,
  children,
}) => {
  const { jobsByOrchestrationId, isLoading, isFetching } = useJobsByOrchestrationIds({
    studyId,
    orchestrationIds,
  });

  const contextValue = useMemo(
    () => ({ jobsByOrchestrationId, isLoadingJobs: isLoading, isFetchingJobs: isFetching }),
    [jobsByOrchestrationId, isLoading, isFetching]
  );

  return <JobsByOrchestrationIdContext.Provider value={contextValue}>{children}</JobsByOrchestrationIdContext.Provider>;
};
